import React, { useCallback, useMemo } from 'react';
import { S2Checkbox } from 'apps/sigma2-ui/src/components/ui/Checkbox';
import { WorkflowWriteBackMode } from 'libs/end-to-end/src/types/databases/workflows/workflow-write-back-mode';
import { ConfigureWriteBackMode } from 'apps/sigma2-ui/src/components/workflows/ConfigureWriteBackMode';
import { WorkflowAIInterviewerV2WriteBackDetails } from 'libs/end-to-end/src/types/databases/workflows/ai-interviewer-v2-write-back/workflow-ai-interviewer-v2-write-back';
import { ConfigureWorkflowBaseProps } from 'apps/sigma2-ui/src/components/workflows/types';
import { ConfigureWriteBackLeverStrategy } from 'apps/sigma2-ui/src/components/workflows/ConfigureWriteBackLeverStrategy';
import { SelectBoxWithSearch } from 'apps/sigma2-ui/src/components/ui/SelectBoxWithSearch';
import { WorkflowAIInterviewerV2WriteBackStrategy } from 'libs/end-to-end/src/types/databases/workflows/ai-interviewer-v2-write-back/workflow-ai-interviewer-v2-write-back';
import { ConfigureWriteBackWorkdayViaWsStrategy } from 'apps/sigma2-ui/src/components/workflows/ConfigureWriteBackWorkdayViaWsStrategy';
import { isCompositeDataSet } from 'libs/end-to-end/src/is-composite-data-set';
import { WorkflowCompositeDataSetSelector } from 'apps/sigma2-ui/src/components/workflows/WorkflowCompositeDataSetSelector';

export interface ConfigureAIInterviewerWriteBackDetailsProps
  extends ConfigureWorkflowBaseProps {
  details: WorkflowAIInterviewerV2WriteBackDetails | null;
  updateDetails: (
    details: WorkflowAIInterviewerV2WriteBackDetails | null,
  ) => void;
}

export interface ConfigureAIInterviewerWriteBackStrategyProps
  extends ConfigureWorkflowBaseProps {
  strategy: WorkflowAIInterviewerV2WriteBackStrategy | null;
  updateStrategy: (
    strategy: WorkflowAIInterviewerV2WriteBackStrategy | null,
  ) => void;
}

export const ConfigureAIInterviewerWriteBackStrategy = ({
  strategy,
  updateStrategy,
  listSecretsPattern,
  organizationId,
  listDataSetsPattern,
}: ConfigureAIInterviewerWriteBackStrategyProps) => {
  const setSelectedType = useCallback(
    (selected: 'none' | WorkflowAIInterviewerV2WriteBackStrategy['type']) => {
      if (selected === 'none') {
        updateStrategy(null);
      } else if (selected !== strategy?.type) {
        updateStrategy({
          type: selected,
        });
      }
    },
    [strategy, updateStrategy],
  );

  const selectedType = useMemo(() => {
    return strategy?.type ?? 'none';
  }, [strategy]);

  return (
    <div>
      <SelectBoxWithSearch
        type="single"
        list={[
          { optionLabel: 'None', optionValue: 'none' },
          { optionLabel: 'Lever', optionValue: 'lever' },
          { optionLabel: 'Workday', optionValue: 'workdayViaWs' },
        ]}
        label="Select a strategy"
        selected={selectedType}
        setSelected={setSelectedType}
      />

      {strategy?.type === 'workdayViaWs' && (
        <ConfigureWriteBackWorkdayViaWsStrategy
          strategy={strategy}
          updateStrategy={updateStrategy}
          listSecretsPattern={listSecretsPattern}
          organizationId={organizationId}
          listDataSetsPattern={listDataSetsPattern}
        />
      )}

      {strategy?.type === 'lever' && (
        <ConfigureWriteBackLeverStrategy
          strategy={strategy}
          updateStrategy={updateStrategy}
          listSecretsPattern={listSecretsPattern}
          organizationId={organizationId}
          listDataSetsPattern={listDataSetsPattern}
        />
      )}
    </div>
  );
};

export const ConfigureAIInterviewerWriteBackDetails = ({
  details,
  updateDetails,
  listDataSetsPattern,
  listSecretsPattern,
  organizationId,
}: ConfigureAIInterviewerWriteBackDetailsProps) => {
  const updateDetailsPartial = useCallback(
    (partial: Partial<WorkflowAIInterviewerV2WriteBackDetails>) => {
      updateDetails({
        type: 'aiInterviewerV2WriteBack',
        ...details,
        ...partial,
      });
    },
    [details, updateDetails],
  );

  const toggleWriteBackEnabled = useCallback(() => {
    updateDetails(
      details
        ? null
        : {
            type: 'aiInterviewerV2WriteBack',
          },
    );
  }, [details, updateDetails]);

  const updateMode = useCallback(
    (mode: WorkflowWriteBackMode) => {
      updateDetailsPartial({
        mode,
      });
    },
    [updateDetailsPartial],
  );

  const updateStrategy = useCallback(
    (strategy: WorkflowAIInterviewerV2WriteBackStrategy | null) => {
      updateDetailsPartial({
        strategy: strategy ?? { type: 'workdayViaWs' },
      });
    },
    [updateDetailsPartial],
  );

  const updateNumberOfSamplesToSend = useCallback(
    (numberOfSamplesToSend: number | undefined) => {
      updateDetailsPartial({
        numberOfSamplesToSend:
          numberOfSamplesToSend !== undefined && isNaN(numberOfSamplesToSend)
            ? undefined
            : numberOfSamplesToSend,
      });
    },
    [updateDetailsPartial],
  );

  const updateNumPreviousDaysOfApplicants = useCallback(
    (numPreviousDaysOfApplicants: number | undefined) => {
      updateDetailsPartial({
        numPreviousDaysOfApplicants:
          numPreviousDaysOfApplicants !== undefined &&
          isNaN(numPreviousDaysOfApplicants)
            ? undefined
            : numPreviousDaysOfApplicants,
      });
    },
    [updateDetailsPartial],
  );

  return (
    <div className="space-y-6 p-6 bg-white rounded-lg shadow-md">
      <h2 className="text-xl font-semibold">
        Configure AI Interviewer Write Back
      </h2>
      <S2Checkbox checked={Boolean(details)} onChange={toggleWriteBackEnabled}>
        Enable the AI Interviewer write back
      </S2Checkbox>

      {details && (
        <>
          <ConfigureWriteBackMode
            mode={details.mode ?? null}
            updateMode={updateMode}
          />
          <WorkflowCompositeDataSetSelector
            dataSetId={details?.dataSetId ?? ''}
            title="Select a composite data set"
            listDataSetsPattern={listDataSetsPattern}
            setDataSetId={(dataSetId) => {
              updateDetailsPartial({
                ...details,
                dataSetId,
              });
            }}
            filterFn={isCompositeDataSet}
          />

          <ConfigureAIInterviewerWriteBackStrategy
            strategy={details.strategy ?? null}
            updateStrategy={updateStrategy}
            listSecretsPattern={listSecretsPattern}
            organizationId={organizationId}
            listDataSetsPattern={listDataSetsPattern}
          />
          <div className="space-y-4">
            <label className="space-y-1 flex flex-col">
              <span>Number of Samples To Send</span>
              <input
                type="number"
                value={details.numberOfSamplesToSend ?? ''}
                onChange={(e) => {
                  updateNumberOfSamplesToSend(e.target.valueAsNumber);
                }}
                className="form-input border rounded p-2"
              />
            </label>

            <label className="space-y-1 flex flex-col">
              <span>Number of Previous Days of Applicants</span>
              <input
                type="number"
                value={details.numPreviousDaysOfApplicants ?? ''}
                onChange={(e) => {
                  updateNumPreviousDaysOfApplicants(e.target.valueAsNumber);
                }}
                className="form-input border rounded p-2"
              />
              <p className="text-sm text-gray-500">
                Specify how many days back to look for applicants to process.
                Leave empty to process all applicants.
              </p>
            </label>
          </div>
        </>
      )}
    </div>
  );
};
